import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CustomQuestion, JoiEvent, Ranking } from 'globals/store/events.data';
import { Wrapper } from 'src/app/components/wrapper.component';
import { Dictionary } from '@ngrx/entity';
import { makeMapFromListOfObjectsAndKey } from 'globals/helpers/helpers';
import { titleCase } from '../../helpers/helpers';
import { CoreEntityType } from 'globals/store/share.data';
import { readOnlyReselector } from '../../store/reselectors/permissions.reselector';
import { CustomAnswerColumnData, customAnswerColumns } from '../../helpers/table-column-types';
import { customQuestionsTableReselector, CustomQuestionsTableReselectorData, entityTypesReselector } from '../../store/reselectors/entity.reselector';
import { TableColumn } from '../../helpers/table.data';
import { NestedTableComponent } from '../nested-table/nested-table.component';
import { saveTable } from '../../modules/performers/performerSharedFunctions';
import { ManagePerformerComponent, ManagePerformerData } from '../../modules/manage/manage-performer.component';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { ManageResourceData, ManageResourceDialogComponent } from '../../modules/resources/manage-resource';
import { ManageExhibitorData, ManageExhibitorDialogComponent } from '../../modules/exhibitors/manage-exhibitor';
import { Observable } from 'rxjs/internal/Observable';
import { ManageAttendeeData, ManageAttendeeDialogComponent } from '../../modules/attendees/manage-attendee.component';
import { ActionButtonComponent } from '../nav-bar/action-button.component';
import { EntityTypeDropdownComponent } from './entity-type-dropdown.component';
import { TraceNameType } from 'src/app/services/performance.tracking.data';
import { entitiesActions, entitiesFeature } from 'src/app/store/entity.era';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'custom-question-answers',
    template: `
<div class="menu">
  <type-select-dropdown *ngIf="entityType"
    [ids]="typeIds"
    [label]="'Question types: '"
    [types]="entityTypes"
    [entityType]="entityType"
    (selectIds)="updateTypeIds($event)"
  ></type-select-dropdown>
  <div style="flex:1;"></div>
  <action-button id="export-table" [text]="'Export'" (click)="exportTable()"></action-button>
</div>
<div *ngIf="noPossibleQuestions" class="empty">
  There are no questions for the selected {{ entityType + ' type' + (typeIds.length !== 1 ? 's' : '') }}.
</div>
<nested-table *ngIf="!noPossibleQuestions"
  [items]="items"
  [columns]="columns"
  [columnsById]="columnsById"
  [readOnly]="true"
  [defaultSelectedColumnId]="customAnswerColumns?.entityName"
  [emptyMessage]="'No custom questions for ' + entityType + 's in this event with the selected types'"
  [noResultsMessage]="'No possible answers exist for the selected ' + entityType + ' type' + (typeIds.length > 1 ? 's' : '')"
  [hadItemsBeforeFiltering]="!!questions?.length"
  (selectItem)="manageCustomQuestionEntity($event)"
></nested-table>
`,
    styles: [`
    .menu { display: flex; margin: 20px 0 0px 5px; }
    .empty { margin: 20px auto; }
    :host ::ng-deep action-button .mat-button.mat-primary { min-width: unset !important; }
    nested-table { margin-top: 20px; }
  `],
    standalone: true,
    imports: [NestedTableComponent, CommonModule, ActionButtonComponent, EntityTypeDropdownComponent],
})
export class CustomQuestionAnswersComponent extends Wrapper<CustomQuestionAnswersComponent> implements OnInit {
  @Input() entityType: CoreEntityType
  entityTitle: string
  traceName: TraceNameType
  event: JoiEvent
  entityTypes: Dictionary<Ranking> = {}
  readOnly: boolean = true
  typeIds: string[] = []
  customAnswerColumns = customAnswerColumns
  items: CustomAnswerColumnData[] = []
  questions: CustomQuestion[] = []
  columns: TableColumn[] = []
  columnsById: Dictionary<TableColumn> = {}
  noPossibleQuestions = false
  @ViewChild(NestedTableComponent) nestedTable: NestedTableComponent<CustomAnswerColumnData>

  exportTable() {
    saveTable(`${this.entityType} answers` ,`${this.event.title} ${this.entityType} answers table`, this.dialog, this.nestedTable, 'export-table')
  }

  updateTypeIds(ids: string[]) {
    this.store.dispatch(entitiesActions.setTypeIds(ids))
  }

  async manageCustomQuestionEntity(row: CustomAnswerColumnData) {
    let promise: Promise<any>
    switch (this.entityType) {
      case 'performer': {
        const data: ManagePerformerData = { performerId: row.id, readOnly: !this.event || this.event.is_readonly }
        promise = openDialogGetResult(this.dialog, ManagePerformerComponent, { disableClose: true, data, panelClass: 'full-screen-modal' })
        break
      }
      case 'exhibitor': {
        const data: ManageExhibitorData = { exhibitorId: row.id }
        promise = openDialogGetResult(this.dialog, ManageExhibitorDialogComponent, { autoFocus: false, disableClose: true, data, panelClass: 'full-screen-modal' })
        break
      }
      case 'attendee': {
        const data: ManageAttendeeData = { attendeeId: row.id }
        promise = openDialogGetResult(this.dialog, ManageAttendeeDialogComponent, { autoFocus: false, disableClose: true, data, panelClass: 'full-screen-modal' })
        break
      }
      case 'resource': {
        const data: ManageResourceData = { resourceId: row.id }
        promise = openDialogGetResult(this.dialog, ManageResourceDialogComponent, { autoFocus: false, disableClose: true, data, panelClass: 'full-screen-modal' })
        break
      }
      default:
        console.log(`Missing handling for type ${this.entityType}`)
        return
    }
    await promise
  }

  ngOnInit() {
    this.selectCallback(customQuestionsTableReselector, this.entityType, (data: CustomQuestionsTableReselectorData) => {
      if (!data) return
      this.columns = data.columns
      this.columnsById = makeMapFromListOfObjectsAndKey(this.columns, 'id')
      this.items = data.items
      this.noPossibleQuestions = !data.questions.length
    })
    this.selectCallback(entityTypesReselector, this.entityType, (typesData: { questions: CustomQuestion[] })   => {
      if (!typesData) return
      this.questions = typesData.questions
    })
    this.subProp(this.store.select('events','event'), 'event')
    this.entityTitle = titleCase(this.entityType)
    this.uiService.changePageTitle(`${this.entityTitle} Custom Questions`, true)

    let typesObservable: Observable<{ [id:string]: Ranking }>
    switch (this.entityType) {
      case 'performer':
        typesObservable = this.store.select('performers', 'performerTypes')
        break
      case 'exhibitor':
        typesObservable = this.store.select('exhibitors', 'exhibitorTypes')
        break
      case 'resource':
        typesObservable = this.store.select('resources', 'resourceTypes')
        break
      case 'attendee':
        typesObservable = this.store.select('attendees', 'attendeeTypes')
        break
      default:
        console.log(`Missing information initialization for ${this.entityType}`)
        return
    }

    this.subProp(typesObservable, 'entityTypes')
  }

  constructor() {
    super()
    this.store.dispatch(entitiesActions.setTypeIds([]))
    this.subProp(this.store.select(entitiesFeature.selectTypeIds), 'typeIds')
    this.selectProp(readOnlyReselector, null, 'readOnly')
  }

}